"use client";
import React from "react";
import { cn } from "../lib/utils";
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
  ResponsiveDialogDescription as DialogDescription,
  ResponsiveDialogFooter as DialogFooter,
} from "./ui/responsive-dialog";
import { Button } from "./ui/button";
import { Calendar as CalendarComponent } from "./ui/calendar";
import { motion } from "framer-motion";
import PaymentMethodCollection from "./PaymentMethodCollection";

const formatCurrency = (value) => {
  const numeric = Number(value);
  const formatter = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  });
  return formatter.format(Number.isFinite(numeric) ? numeric : 0);
};

export default function EnhancedBookingModal({
  open,
  onOpenChange,
  therapist,
  // Services
  availableServices,
  selectedService,
  onServiceSelect,
  isLoadingServices,
  // Duration
  selectedDuration,
  onDurationSelect,
  // Settings
  selectedSetting,
  onSettingSelect,
  // Date & Time
  bookingDate,
  onDateSelect,
  bookingTime,
  onTimeSelect,
  availableSlots,
  isLoadingSlots,
  // Payment Options
  paymentOption,
  onPaymentOptionChange,
  savedCard,
  paymentInfo,
  onPaymentSuccess,
  bookingError,
  onErrorChange,
  clientEmail,
  clientName,
  // Free Massage
  freeMassagesAvailable,
  useFreeMassage,
  onUseFreeMassageChange,
  loadingRewards,
  // Gift Card
  giftCardCode,
  onGiftCardCodeChange,
  onValidateGiftCard,
  giftCardValidating,
  giftCardValid,
  giftCardError,
  giftCardAmount,
  giftCardApplied,
  // Confirmation
  onConfirm,
  isSubmitting,
}) {
  const therapistSettings = therapist?.settings || therapist?.availableSettings || [];
  const hasMultipleSettings = therapistSettings.length > 1;

  // Calculate pricing based on selected duration
  const servicePrice = selectedDuration?.price || 0;
  const giftCardDiscount = giftCardApplied ? Math.min(giftCardAmount, servicePrice) : 0;
  const subtotal = servicePrice - giftCardDiscount;
  const gstAmount = subtotal * 0.05;
  const total = subtotal + gstAmount;
  const isFullyCovered = useFreeMassage || (giftCardApplied && subtotal <= 0);

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-palette-phantom">
            Book with {therapist?.name}
          </DialogTitle>
          <DialogDescription className="text-palette-space">
            Select your preferences below to complete your booking
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Setting Selection - Show if therapist has settings */}
          {therapistSettings.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-semibold text-palette-phantom">
                  {hasMultipleSettings ? "Select Location" : "Location"}
                </label>
                {hasMultipleSettings && (
                  <span className="text-xs text-palette-space">
                    Choose where you'd like your session
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {therapistSettings.map((setting) => {
                  const settingInfo = {
                    Home: { icon: "🏠", label: "Your Home", desc: "Therapist comes to you" },
                    Mobile: { icon: "🚗", label: "Mobile", desc: "Therapist travels to you" },
                    Clinic: { icon: "🏥", label: "Clinic", desc: "Visit the clinic" },
                  };
                  const info = settingInfo[setting] || { icon: "", label: setting, desc: "" };

                  return (
                    <button
                      key={setting}
                      onClick={() => onSettingSelect(setting)}
                      disabled={!hasMultipleSettings}
                      className={cn(
                        "p-5 rounded-xl border-2 transition-all",
                        selectedSetting === setting
                          ? "border-palette-lime bg-gradient-to-br from-palette-lime/20 to-palette-lime/5 shadow-sm"
                          : hasMultipleSettings
                          ? "border-palette-smoke hover:border-palette-navy/30 hover:shadow-sm"
                          : "border-palette-smoke bg-gray-50 cursor-default"
                      )}>
                      <div className="flex flex-col items-center text-center gap-2">
                        <span className="text-3xl">{info.icon}</span>
                        <div>
                          <div className={cn(
                            "font-semibold text-sm",
                            selectedSetting === setting
                              ? "text-palette-phantom"
                              : "text-palette-space"
                          )}>
                            {info.label}
                          </div>
                          <div className="text-xs text-palette-space mt-0.5">
                            {info.desc}
                          </div>
                        </div>
                        {selectedSetting === setting && (
                          <div className="mt-1 flex items-center justify-center w-6 h-6 rounded-full bg-palette-lime">
                            <span className="text-palette-navy text-sm">✓</span>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Service Selection */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: therapistSettings.length > 0 ? 0.1 : 0 }}
            className="space-y-3">
            <label className="block text-sm font-semibold text-palette-phantom">
              Select Service
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {isLoadingServices ? (
                <p className="text-sm text-palette-space col-span-full">
                  Loading services...
                </p>
              ) : availableServices.length > 0 ? (
                availableServices.map((service) => (
                  <button
                    key={service._id || service.id}
                    onClick={() => onServiceSelect(service)}
                    className={cn(
                      "p-4 rounded-xl border-2 text-left transition-all",
                      selectedService?._id === service._id || selectedService?.id === service.id
                        ? "border-palette-lime bg-palette-lime/10"
                        : "border-palette-smoke hover:border-palette-navy/30"
                    )}>
                    <div className="font-semibold text-palette-phantom">
                      {service.name}
                    </div>
                    <div className="text-sm text-palette-space mt-1">
                      {service.description || "Professional massage therapy"}
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-sm text-palette-space col-span-full">
                  No services available for this therapist
                </p>
              )}
            </div>
          </motion.div>

          {/* Duration Selection */}
          {selectedService && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="space-y-3">
              <label className="block text-sm font-semibold text-palette-phantom">
                Select Duration
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {selectedService.durations?.map((duration) => (
                  <button
                    key={duration.duration}
                    onClick={() => onDurationSelect(duration)}
                    className={cn(
                      "p-4 rounded-xl border-2 text-center transition-all",
                      selectedDuration?.duration === duration.duration
                        ? "border-palette-lime bg-palette-lime/10"
                        : "border-palette-smoke hover:border-palette-navy/30"
                    )}>
                    <div className="font-semibold text-palette-phantom">
                      {duration.duration} minutes
                    </div>
                    <div className="text-sm text-palette-space mt-1">
                      {formatCurrency(duration.price)}
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Date Selection */}
          {selectedService && selectedDuration && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="space-y-3">
              <label className="block text-sm font-semibold text-palette-phantom">
                Select Date
              </label>
              <CalendarComponent
                mode="single"
                selected={bookingDate}
                onSelect={onDateSelect}
                disabled={(date) => date < new Date()}
                className="rounded-xl border border-palette-smoke bg-white"
              />
            </motion.div>
          )}

          {/* Time Selection */}
          {selectedService && selectedDuration && bookingDate && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-3">
              <label className="block text-sm font-semibold text-palette-phantom">
                Select Time
              </label>
              {isLoadingSlots ? (
                <p className="text-sm text-palette-space">Loading available times...</p>
              ) : availableSlots.length > 0 ? (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => onTimeSelect(slot)}
                      className={cn(
                        "py-2 px-3 rounded-lg border text-sm font-medium transition-all",
                        bookingTime === slot
                          ? "border-palette-lime bg-palette-lime text-palette-navy"
                          : "border-palette-smoke hover:border-palette-navy/30"
                      )}>
                      {slot}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-palette-space">
                  No available times for this date
                </p>
              )}
            </motion.div>
          )}

          {/* Booking Summary */}
          {selectedService && selectedDuration && bookingDate && bookingTime && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="bg-palette-lime/10 border border-palette-lime/30 rounded-xl p-5">
              <h3 className="font-semibold text-palette-phantom mb-3">
                Booking Summary
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-palette-space">Service:</span>
                  <span className="font-medium text-palette-phantom">
                    {selectedService.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-palette-space">Duration:</span>
                  <span className="font-medium text-palette-phantom">
                    {selectedDuration.duration} minutes
                  </span>
                </div>
                {hasMultipleSettings && selectedSetting && (
                  <div className="flex justify-between">
                    <span className="text-palette-space">Setting:</span>
                    <span className="font-medium text-palette-phantom">
                      {selectedSetting}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-palette-space">Date:</span>
                  <span className="font-medium text-palette-phantom">
                    {bookingDate.toLocaleDateString("en-US", {
                      weekday: "long",
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-palette-space">Time:</span>
                  <span className="font-medium text-palette-phantom">
                    {bookingTime}
                  </span>
                </div>
                <div className="pt-2 border-t border-palette-lime/30 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-palette-space">Subtotal:</span>
                    <span className="text-palette-phantom">
                      {formatCurrency(servicePrice)}
                    </span>
                  </div>
                  {giftCardApplied && giftCardDiscount > 0 && (
                    <div className="flex justify-between text-xs text-emerald-600">
                      <span>Gift card discount:</span>
                      <span>-{formatCurrency(giftCardDiscount)}</span>
                    </div>
                  )}
                  {!useFreeMassage && !isFullyCovered && (
                    <div className="flex justify-between text-xs">
                      <span className="text-palette-space">GST (5%):</span>
                      <span className="text-palette-phantom">
                        {formatCurrency(gstAmount)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between pt-1 border-t border-palette-lime/30">
                    <span className="font-semibold text-palette-phantom">Total:</span>
                    <span className="font-bold text-palette-phantom">
                      {useFreeMassage ? (
                        <span className="text-purple-600">FREE</span>
                      ) : isFullyCovered ? (
                        <span className="text-emerald-600">COVERED</span>
                      ) : (
                        formatCurrency(total)
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Free Massage Redemption */}
          {!loadingRewards && freeMassagesAvailable > 0 && selectedService && selectedDuration && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="rounded-xl border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50 p-5">
              <label className="flex items-start gap-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useFreeMassage}
                  onChange={(e) => onUseFreeMassageChange(e.target.checked)}
                  className="mt-1 h-5 w-5 text-purple-600 focus:ring-purple-500 rounded"
                />
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-purple-900">
                      Redeem a free massage
                    </span>
                    <span className="rounded-full bg-purple-200 px-2 py-1 text-xs font-semibold text-purple-700">
                      {freeMassagesAvailable} available
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-purple-700">
                    You've earned {freeMassagesAvailable} free {freeMassagesAvailable === 1 ? "massage" : "massages"}! Check this box to use one now.
                  </p>
                </div>
              </label>
            </motion.div>
          )}

          {/* Gift Card */}
          {!useFreeMassage && selectedService && selectedDuration && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="space-y-3">
              <label className="block text-sm font-semibold text-palette-phantom">
                Gift card or promo code
              </label>
              <p className="text-xs text-palette-space">
                Apply a BookaTherapy gift card or promo code
              </p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="text"
                  value={giftCardCode}
                  onChange={(e) => onGiftCardCodeChange(e.target.value.toUpperCase())}
                  placeholder="GIFT-2025"
                  className={cn(
                    "flex-1 rounded-xl border-2 px-4 py-3 transition",
                    giftCardValid === true
                      ? "border-emerald-500 bg-emerald-50"
                      : giftCardValid === false
                      ? "border-red-300 bg-red-50"
                      : "border-palette-smoke bg-white",
                    "focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  )}
                />
                <button
                  type="button"
                  onClick={onValidateGiftCard}
                  disabled={giftCardValidating || !giftCardCode.trim()}
                  className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:from-emerald-700 hover:to-teal-700 disabled:cursor-not-allowed disabled:opacity-60">
                  {giftCardValidating ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    "Redeem"
                  )}
                </button>
              </div>
              {giftCardError && (
                <p className="text-sm text-red-600">{giftCardError}</p>
              )}
              {giftCardValid === true && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
                  Gift card applied — {isFullyCovered ? "your booking is fully covered." : `${formatCurrency(giftCardDiscount)} discount applied`}
                </div>
              )}
            </motion.div>
          )}

          {/* Payment Options */}
          {!useFreeMassage && !isFullyCovered && selectedService && selectedDuration && savedCard && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-3">
              <h3 className="text-sm font-semibold text-palette-phantom">
                Payment option
              </h3>
              <p className="text-xs text-palette-space">
                Your card ending in {savedCard.last4} is on file
              </p>
              <div className="space-y-3">
                <label
                  className={cn(
                    "flex items-start gap-4 rounded-xl border-2 p-4 transition cursor-pointer",
                    paymentOption === "payAtClinic"
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-palette-smoke bg-white hover:border-palette-navy/30"
                  )}>
                  <input
                    type="radio"
                    name="paymentOption"
                    value="payAtClinic"
                    checked={paymentOption === "payAtClinic"}
                    onChange={(e) => onPaymentOptionChange(e.target.value)}
                    className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-palette-phantom">
                        Pay at clinic
                      </span>
                      <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">
                        Most popular
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-palette-space">
                      Keep your card on file. Pay in person after your appointment.
                    </p>
                  </div>
                </label>

                <label
                  className={cn(
                    "flex items-start gap-4 rounded-xl border-2 p-4 transition cursor-pointer",
                    paymentOption === "payUpfront"
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-palette-smoke bg-white hover:border-palette-navy/30"
                  )}>
                  <input
                    type="radio"
                    name="paymentOption"
                    value="payUpfront"
                    checked={paymentOption === "payUpfront"}
                    onChange={(e) => onPaymentOptionChange(e.target.value)}
                    className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-palette-phantom">
                        Pay upfront
                      </span>
                      <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-700">
                        Instant receipt
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-palette-space">
                      Complete payment now and receive your insurance-ready receipt immediately.
                    </p>
                  </div>
                </label>
              </div>
            </motion.div>
          )}

          {/* Payment Collection - Show when payment needed AND (no saved card OR pay upfront selected) */}
          {!useFreeMassage && !isFullyCovered && selectedService && selectedDuration &&
           (!savedCard || paymentOption === "payUpfront") && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="space-y-3">
              <h3 className="text-sm font-semibold text-palette-phantom">
                {paymentOption === "payUpfront" ? "Complete Payment" : "Payment Method"}
              </h3>
              <div className="rounded-xl border-2 border-palette-smoke bg-white p-5">
                <PaymentMethodCollection
                  clientEmail={clientEmail}
                  clientName={clientName}
                  paymentOption={paymentOption}
                  onSuccess={onPaymentSuccess}
                  onError={onErrorChange}
                  onConfirmBooking={onConfirm}
                  submitting={isSubmitting}
                  adjustedAmount={Math.round(total * 100)} // Convert to cents
                />
              </div>
              {bookingError && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {bookingError}
                </div>
              )}
            </motion.div>
          )}
        </div>

        <DialogFooter className="border-t pt-4 gap-2">
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          {/* Only show confirm button if:
              1. Using free massage/fully covered OR
              2. Using saved card (not pay upfront) OR
              3. Payment info collected (from PaymentMethodCollection)
          */}
          {(useFreeMassage || isFullyCovered || (savedCard && paymentOption === "payAtClinic") || paymentInfo) && (
            <Button
              className="bg-palette-navy hover:bg-palette-phantom text-white"
              disabled={
                !selectedService ||
                !selectedDuration ||
                !bookingDate ||
                !bookingTime ||
                isSubmitting
              }
              onClick={onConfirm}>
              {isSubmitting ? "Processing..." : "Confirm Booking"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
